import joinUrl from 'url-join';
import ajaxPromises from 'common/js/AjaxPromises';
import globalConfig from 'common/js/config';
import { generateUrlWithRegion } from 'common/js/url-utils';
import { CreativeTagTrackingPayload, PayloadDatas } from '../../types';
import { CreativeTagEvent } from '../../enums';
import {
  isComplexPayloadResult,
  PayloadBuilderStrategy,
  SingleResult
} from '../../payload-builder-strategies/payload-builder-strategy';

export class CreativeTrackingService {
  private readonly baseUrl = joinUrl(
    generateUrlWithRegion(globalConfig.MS_ADS_MONITORING_EVENTS_URL),
    'ocs'
  );

  private allreadySentEvents: {
    [key in CreativeTagEvent]?: boolean;
  } = {
    [CreativeTagEvent.CreativeLoadingFailed]: false
  };

  constructor(
    private readonly payloadBuilderStrategy: PayloadBuilderStrategy
  ) {}

  async sendCreativeTagTracking(
    creativeTagEvent: CreativeTagEvent,
    payload?: PayloadDatas,
    evt?: CustomEvent
  ): Promise<void> {
    if (
      this.allreadySentEvents[creativeTagEvent] !== undefined &&
      this.allreadySentEvents[creativeTagEvent]
    ) {
      return;
    }

    let eventPayload = this.payloadBuilderStrategy.buildPayload(
      creativeTagEvent,
      payload,
      evt
    );

    if (this.allreadySentEvents[creativeTagEvent] !== undefined) {
      this.allreadySentEvents[creativeTagEvent] = true;
    }

    if (Array.isArray(eventPayload)) {
      await Promise.all(eventPayload.map(this.sendPayload.bind(this)));
    } else {
      await this.sendPayload(eventPayload);
    }
  }

  protected async sendPayload(eventPayload: SingleResult): Promise<void> {
    let baseUrl = this.baseUrl;
    let method: 'GET' | 'POST' = 'GET';
    if (isComplexPayloadResult(eventPayload)) {
      baseUrl = eventPayload.endPoint.url;
      method = eventPayload.endPoint.method;
      eventPayload = eventPayload.payload;
    }

    if (method === 'GET') {
      await this.sendGetRequest(baseUrl, eventPayload);
    } else {
      await this.sendPostRequest(baseUrl, eventPayload);
    }
  }

  protected sendPostRequest(
    baseUrl: string,
    eventPayload: CreativeTagTrackingPayload
  ): Promise<void> {
    return ajaxPromises.postJson(baseUrl, eventPayload).catch(console.error);
  }

  protected sendGetRequest(
    baseUrl: string,
    eventPayload: CreativeTagTrackingPayload
  ): Promise<void> {
    const queryParts: string[] = [];
    Object.entries(eventPayload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        let strValue: string;
        if (typeof value === 'object' && !Array.isArray(value)) {
          strValue = encodeURIComponent(JSON.stringify(value));
        } else {
          strValue = encodeURIComponent(String(value));
        }
        queryParts.push(`${encodeURIComponent(key)}=${strValue}`);
      }
    });
    const urlWithParams = `${baseUrl}?${queryParts.join('&')}`;

    return ajaxPromises.get(urlWithParams).catch(console.error);
  }
}
